/**
 * Like Handler - Event Handler Implementation
 * Handles like button click interactions
 */

import { LikeApiService } from "./like-api";
import type { LikeApiResponse } from "./like-api";
import { LikeForm } from "./like-form";
import { modalHandler } from "../modal/modal-handler";
import { phpPostData } from "../core/php-data-access";
import { toPositiveIntOrNull } from "../util/id-utils";

interface LikeApiLike {
    performLikeActionWithRetry(
        isLiked: boolean,
        boardId: string,
        postId: number,
        commentId: number | undefined,
        scope: string,
    ): Promise<LikeApiResponse>;
}

interface LikeRenderLike {
    getLikesCount(button: HTMLElement): number;
    updateLikeUI(button: HTMLElement, isLiked: boolean, likesCount: number): void;
    setLoadingState(button: HTMLElement): void;
    clearLoadingState(button: HTMLElement): void;
    showError(message: string): void;
}

interface ModalHandlerLike {
    isOpen?: (() => boolean) | boolean;
    state?: {
        currentPostId?: number;
        syncToParent(postId: number, mode: string, field: string, value: unknown): void;
        syncCommentLikeToParent(postId: number, commentId: number, isLiked: boolean, likesCount: number): void;
    };
}

class LikeHandler {
    public apiService: LikeApiLike;

    public likeRender: LikeRenderLike;

    public likeForm: LikeForm;

    public constructor(apiService: LikeApiLike = LikeApiService, likeRender: LikeRenderLike, likeForm: LikeForm) {
        this.apiService = apiService;
        this.likeRender = likeRender;
        this.likeForm = likeForm;
    }

    private updatePostDataLikeState(postId: number, isLiked: boolean, likesCount: number): void {
        const postData = phpPostData(postId) as Record<string, unknown> | null;
        if (!postData) {
            return;
        }

        postData.likes_count = likesCount;
        postData.is_liked_by_current_user = isLiked;
    }

    private syncOpenModalLikeOptimistically(postId: number, isLiked: boolean, likesCount: number): HTMLElement | null {
        const modalLikeBtn = this.likeForm.getModalLikeButton(postId);
        if (!(modalLikeBtn instanceof HTMLElement)) {
            return null;
        }

        this.likeRender.updateLikeUI(modalLikeBtn, isLiked, likesCount);
        return modalLikeBtn;
    }

    public async handleLike(button: HTMLElement): Promise<void> {
        const boardId = String(button.dataset.boardId || "");
        const postId = toPositiveIntOrNull(button.dataset.postId);
        const commentId = toPositiveIntOrNull(button.dataset.commentId) ?? undefined;
        const scope = button.dataset.likeScope || "post";

        if (postId === null) {
            return;
        }

        const isLiked = this.likeForm.isLiked(button);

        if (this.likeForm.isBusy(button)) {
            return;
        }

        const originalIsLiked = isLiked;
        const originalCount = this.likeRender.getLikesCount(button);
        const originalPostData = phpPostData(postId) as Record<string, unknown> | null;
        const originalPostDataLikeCount = Number(originalPostData?.likes_count ?? originalCount);
        const originalPostDataIsLiked = Boolean(originalPostData?.is_liked_by_current_user ?? originalIsLiked);

        const optimisticIsLiked = !isLiked;
        const optimisticCount = isLiked ? originalCount - 1 : originalCount + 1;
        this.likeRender.updateLikeUI(button, optimisticIsLiked, optimisticCount);

        let optimisticModalLikeBtn: HTMLElement | null = null;
        if (scope === "post" && !this.likeForm.isInElementModal(button)) {
            this.updatePostDataLikeState(postId, optimisticIsLiked, optimisticCount);
            optimisticModalLikeBtn = this.syncOpenModalLikeOptimistically(postId, optimisticIsLiked, optimisticCount);
        }

        this.likeRender.setLoadingState(button);

        try {
            const response = await this.apiService.performLikeActionWithRetry(
                originalIsLiked,
                boardId,
                postId,
                commentId,
                scope,
            );

            if (response.success) {
                let finalIsLiked: boolean;
                const finalCount = Number(response.likes_count ?? optimisticCount);

                if ("already_liked" in response && response.already_liked) {
                    finalIsLiked = true;
                } else if ("already_unliked" in response && response.already_unliked) {
                    finalIsLiked = false;
                } else {
                    finalIsLiked = optimisticIsLiked;
                }

                this.likeRender.updateLikeUI(button, finalIsLiked, finalCount);

                const isInElementModal = this.likeForm.isInElementModal(button);
                const modalHandlerLike = modalHandler as unknown as ModalHandlerLike | null;

                if (isInElementModal && modalHandlerLike?.state) {
                    const modalState = modalHandlerLike.state;
                    if (scope === "post" && postId) {
                        modalState.syncToParent(postId, "edit", "likes_count", finalCount);
                        modalState.syncToParent(postId, "edit", "is_liked_by_current_user", finalIsLiked);
                    } else if (scope === "comment" && Number.isInteger(commentId) && commentId > 0) {
                        modalState.syncCommentLikeToParent(postId, commentId, finalIsLiked, finalCount);
                    }
                } else if (scope === "post") {
                    this.updatePostDataLikeState(postId, finalIsLiked, finalCount);

                    const isModalOpen =
                        typeof modalHandlerLike?.isOpen === "function"
                            ? modalHandlerLike.isOpen()
                            : Boolean(modalHandlerLike?.isOpen);

                    if (isModalOpen) {
                        const modalLikeBtn = this.likeForm.getModalLikeButton(postId);
                        if (modalLikeBtn instanceof HTMLElement) {
                            this.likeRender.updateLikeUI(modalLikeBtn, finalIsLiked, finalCount);
                        }
                    }
                }
            } else {
                this.likeRender.updateLikeUI(button, originalIsLiked, originalCount);
                if (scope === "post" && !this.likeForm.isInElementModal(button)) {
                    this.updatePostDataLikeState(postId, originalPostDataIsLiked, originalPostDataLikeCount);
                }
                if (optimisticModalLikeBtn instanceof HTMLElement) {
                    this.likeRender.updateLikeUI(optimisticModalLikeBtn, originalIsLiked, originalCount);
                }
                const responseMessage = "message" in response ? response.message : undefined;
                this.likeRender.showError(String(responseMessage || "An error occurred"));
            }
        } catch (error) {
            this.likeRender.updateLikeUI(button, originalIsLiked, originalCount);
            if (scope === "post" && !this.likeForm.isInElementModal(button)) {
                this.updatePostDataLikeState(postId, originalPostDataIsLiked, originalPostDataLikeCount);
            }
            if (optimisticModalLikeBtn instanceof HTMLElement) {
                this.likeRender.updateLikeUI(optimisticModalLikeBtn, originalIsLiked, originalCount);
            }

            if (error instanceof Error && error.message.includes("Server is experiencing issues")) {
                this.likeRender.showError("Server is busy. Please try again in a moment.");
            } else {
                this.likeRender.showError("Something went wrong. Please try again.");
            }
        } finally {
            this.likeRender.clearLoadingState(button);
        }
    }
}

export { LikeHandler };
